import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import LeadsSourcesChart from "@/components/admin/LeadsSourcesChart";
import { Inbox, Newspaper, Plus, ArrowUpRight, TrendingUp, FileEdit } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, to, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group rounded-3xl bg-white border border-neutral-200 p-6 hover:shadow-[0_14px_40px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all"
  >
    <div className="flex items-center justify-between">
      <div className="w-11 h-11 rounded-2xl bg-neutral-100 grid place-items-center text-neutral-700 group-hover:bg-neutral-950 group-hover:text-white transition-colors">
        <Icon className="w-5 h-5" />
      </div>
      <ArrowUpRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-700 transition-colors" />
    </div>
    <div className="mt-6 font-display text-4xl font-light tracking-tighter text-neutral-950">
      {value}
    </div>
    <div className="mt-1 text-sm text-neutral-500">{label}</div>
  </Link>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({ leads: 0, newLeads: 0, posts: 0, drafts: 0 });
  const [allLeads, setAllLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/leads").then((r) => r.data).catch(() => []),
      api.get("/admin/blog/posts").then((r) => r.data).catch(() => []),
    ]).then(([leads, posts]) => {
      setStats({
        leads: leads.length,
        newLeads: leads.filter((l) => l.status === "new").length,
        posts: posts.filter((p) => p.published).length,
        drafts: posts.filter((p) => !p.published).length,
      });
      setAllLeads(leads);
      setRecentLeads(leads.slice(0, 5));
      setRecentPosts(posts.slice(0, 5));
    });
  }, []);

  return (
    <AdminShell
      title="Dashboard"
      subtitle="Übersicht über Leads, Beiträge und Aktivität."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Inbox}
          label="Leads gesamt"
          value={stats.leads}
          to="/admin/leads"
          testId="stat-card-leads"
        />
        <StatCard
          icon={TrendingUp}
          label="Neue Leads"
          value={stats.newLeads}
          to="/admin/leads"
          testId="stat-card-new-leads"
        />
        <StatCard
          icon={Newspaper}
          label="Veröffentlichte Beiträge"
          value={stats.posts}
          to="/admin/blog"
          testId="stat-card-posts"
        />
        <StatCard
          icon={FileEdit}
          label="Entwürfe"
          value={stats.drafts}
          to="/admin/blog"
          testId="stat-card-drafts"
        />
      </div>

      <div className="mt-8">
        <LeadsSourcesChart leads={allLeads} />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent leads */}
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-medium">Letzte Anfragen</h2>
            <Link to="/admin/leads" className="text-xs text-sky-700 hover:underline">
              Alle ansehen
            </Link>
          </div>
          {recentLeads.length === 0 ? (
            <div className="mt-6 text-sm text-neutral-500">Noch keine Anfragen.</div>
          ) : (
            <ul className="mt-5 divide-y divide-neutral-100">
              {recentLeads.map((l) => (
                <li key={l.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{l.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-neutral-100 text-neutral-700">
                    {l.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent posts */}
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-medium">Letzte Beiträge</h2>
            <Link
              to="/admin/blog/new"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-neutral-950 text-white hover:bg-black"
              data-testid="dashboard-new-post-btn"
            >
              <Plus className="w-3 h-3" />
              Neu
            </Link>
          </div>
          {recentPosts.length === 0 ? (
            <div className="mt-6 text-sm text-neutral-500">Noch keine Beiträge.</div>
          ) : (
            <ul className="mt-5 divide-y divide-neutral-100">
              {recentPosts.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between gap-3">
                  <Link
                    to={`/admin/blog/edit/${p.id}`}
                    className="min-w-0 group flex-1"
                  >
                    <div className="text-sm font-medium truncate group-hover:text-sky-700">
                      {p.title?.de}
                    </div>
                    <div className="text-xs text-neutral-500 truncate">/{p.slug}</div>
                  </Link>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                      p.published
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-neutral-100 text-neutral-600"
                    }`}
                  >
                    {p.published ? "Live" : "Draft"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminDashboard;
